import { WarningAmberOutlined } from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SftpCredentialFields, useCopyAllSftpCredentials } from "../../../../../components/UIToolkit/molecules/SftpCredentialFields";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import { type SftpCredentials, useSftpFolderCredentials } from "../sftpHooks";

type Props = {
	open: boolean;
	folder: string;
	onClose: () => void;
	/**
	 * Credentials the caller already holds — from having just created the folder. When passed, the
	 * dialog shows these instead of fetching: a freshly minted password is in hand and re-reading it
	 * would be a second, pointless exposure of the same secret.
	 */
	credentials?: SftpCredentials;
};

/**
 * The SFTP login for one folder, gated behind an explicit "you are about to copy credentials" step.
 *
 * The warning is not decoration: this is the value ops sends to a vendor, and the whole point of the
 * dialog is that handing it to the wrong person grants write access to a payroll feed. Nothing is
 * fetched until the reader agrees, so a mis-click never even pulls the secret into the browser.
 */
export const SftpFolderCredentialsDialog: FC<Props> = ({ open, folder, onClose, credentials }) => {
	const { t } = useTranslation();
	const copyAllCredentials = useCopyAllSftpCredentials();
	const [agreed, setAgreed] = useState(false);
	const { data: fetched, isFetching, error } = useSftpFolderCredentials(folder, open && agreed && !credentials);
	const shown = credentials ?? fetched;

	const handleClose = () => {
		setAgreed(false);
		onClose();
	};

	const copyAll = () => {
		if (shown) copyAllCredentials(shown);
	};

	return (
		<SystemDialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			title={
				agreed ? t("department.config.integrations.sftp.credentials.title", { folder }) : t("department.config.integrations.sftp.credentials.warningTitle")
			}
			body={
				agreed ? (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{isFetching && !shown && <CircularProgress size={20} />}
						{!!error && !shown && (
							<SystemBanner
								tone="error"
								title={t("department.config.integrations.sftp.credentials.error.title")}
								description={t("department.config.integrations.sftp.credentials.error.description")}
								dataTestId="sftp-credentials-error"
							/>
						)}
						{shown && <SftpCredentialFields credentials={shown} dataTestId="sftp-credentials" />}
					</Box>
				) : (
					<SystemBanner
						tone="warning"
						icon={<WarningAmberOutlined />}
						title={t("department.config.integrations.sftp.credentials.warningTitle")}
						description={t("department.config.integrations.sftp.credentials.warningBody")}
						dataTestId="sftp-credentials-warning"
					/>
				)
			}
			cancel={agreed ? t("close") : t("cancel")}
			// The primary action carries the two steps: agree, then copy. So the reader can never reach
			// the copy without having read the warning first.
			confirm={agreed ? t("department.config.integrations.sftp.credentials.copyAll") : t("department.config.integrations.sftp.credentials.agree")}
			confirmButtonVariant="primary"
			confirmDisabled={agreed && !shown}
			onConfirm={agreed ? copyAll : () => setAgreed(true)}
			dataTestId="sftp-credentials-dialog"
		/>
	);
};
